'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  parseMarkdownSections,
  stripSectionNumber,
  parseKeyValueList,
  parseParagraphs,
  type MarkdownSection,
} from './markdown-sections';
import { SectionCard } from '../../definition/templates/section-card';
import { SectionGrid } from '../../definition/templates/section-grid';

interface ArchitectureTemplateProps {
  markdown: string;
  className?: string;
}

const SECTION_META: Record<string, { dot?: string; titleColor?: string; border?: string }> = {
  'architecture overview': { dot: 'bg-indigo-500', titleColor: 'text-indigo-900' },
  'domain model': { dot: 'bg-violet-500', titleColor: 'text-violet-900' },
  'system components': { dot: 'bg-blue-500', titleColor: 'text-blue-900' },
  'data architecture': { dot: 'bg-emerald-500', titleColor: 'text-emerald-900' },
  'integration architecture': { dot: 'bg-cyan-500', titleColor: 'text-cyan-900' },
  'cross-cutting concerns': { dot: 'bg-amber-500', titleColor: 'text-amber-900' },
  'deployment topology': { dot: 'bg-rose-500', titleColor: 'text-rose-900' },
};

function getMeta(title: string) {
  const key = stripSectionNumber(title).toLowerCase();
  return SECTION_META[key] || { dot: 'bg-gray-500', titleColor: 'text-gray-900' };
}

function RichParagraph({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <p className="text-[15px] text-gray-700 leading-relaxed mb-3">
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-semibold text-indigo-700">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code key={i} className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-sm font-mono border border-indigo-100">
              {part.slice(1, -1)}
            </code>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </p>
  );
}

function BodyContent({ body }: { body: string }) {
  if (!body) return null;
  const paragraphs = parseParagraphs(body);
  if (paragraphs.length === 0) return null;

  return (
    <div>
      {paragraphs.map((p, i) => (
        <RichParagraph key={i} text={p} />
      ))}
    </div>
  );
}

function ComponentCard({ section }: { section: MarkdownSection }) {
  const kvItems = parseKeyValueList(section.body);
  const meta = { dot: 'bg-blue-400', titleColor: 'text-blue-900' };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
      <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', meta.dot)} />
        {section.title}
      </h4>
      {kvItems.length > 0 ? (
        <dl className="space-y-2.5">
          {kvItems.map((item, i) => (
            <div key={i}>
              <dt className="text-sm font-semibold text-gray-800">{item.key}</dt>
              <dd className="text-sm text-gray-600 leading-relaxed mt-0.5">
                <RichParagraph text={item.value} />
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <BodyContent body={section.body} />
      )}
    </div>
  );
}

function SystemComponentsSection({ section }: { section: MarkdownSection }) {
  const meta = getMeta(section.title);
  return (
    <SectionCard
      title={stripSectionNumber(section.title)}
      dotColor={meta.dot}
      titleColor={meta.titleColor}
    >
      {section.body && <BodyContent body={section.body} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {section.children.map((child, i) => (
          <ComponentCard key={i} section={child} />
        ))}
      </div>
    </SectionCard>
  );
}

function GenericSection({ section }: { section: MarkdownSection }) {
  const meta = getMeta(section.title);
  return (
    <SectionCard
      title={stripSectionNumber(section.title)}
      dotColor={meta.dot}
      titleColor={meta.titleColor}
    >
      <BodyContent body={section.body} />
      {section.children.length > 0 && (
        <div className="space-y-3 mt-3">
          {section.children.map((child, i) => (
            <div key={i}>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0" />
                {child.title}
              </h4>
              <div className="ml-4">
                <BodyContent body={child.body} />
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function isSystemComponentsSection(title: string): boolean {
  return stripSectionNumber(title).toLowerCase() === 'system components';
}

function shouldPairSections(a: string, b: string): boolean {
  const la = stripSectionNumber(a).toLowerCase();
  const lb = stripSectionNumber(b).toLowerCase();

  const pairs: [string, string][] = [
    ['data architecture', 'integration architecture'],
    ['cross-cutting concerns', 'deployment topology'],
  ];

  return pairs.some(([x, y]) => (la === x && lb === y) || (la === y && lb === x));
}

export function ArchitectureTemplate({ markdown, className }: ArchitectureTemplateProps) {
  const parsed = useMemo(() => parseMarkdownSections(markdown), [markdown]);

  if (parsed.sections.length === 0) {
    return (
      <div className={cn('prose prose-base max-w-none text-gray-700', className)}>
        <p>{markdown}</p>
      </div>
    );
  }

  const rendered: React.ReactNode[] = [];
  const sections = parsed.sections;
  let i = 0;

  while (i < sections.length) {
    const section = sections[i];

    if (isSystemComponentsSection(section.title)) {
      rendered.push(<SystemComponentsSection key={i} section={section} />);
      i++;
      continue;
    }

    if (i + 1 < sections.length && shouldPairSections(section.title, sections[i + 1].title)) {
      rendered.push(
        <SectionGrid key={i}>
          <GenericSection section={section} />
          <GenericSection section={sections[i + 1]} />
        </SectionGrid>
      );
      i += 2;
      continue;
    }

    rendered.push(<GenericSection key={i} section={section} />);
    i++;
  }

  return (
    <div className={cn('space-y-6', className)}>
      {rendered}
    </div>
  );
}
